import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { ExportDialogComponent } from '@core/components/export-dialog/export-dialog.component';
import { ExportDialogComponentInjectedData, ExportDialogTypeEnum } from '@core/components/export-dialog/export-dialog.types';
import { SidenavService } from '@core/services/sidenav.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Equipment } from '@interfaces';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogEquipmentService } from './dialog/dialog-equipments.service';
import { FilterEquipmentsComponent } from './filter/filter-equipments.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { EquipmentsService } from './services/equipments-resolver.service';

@Component({
  selector: 'equipments',
  templateUrl: './equipments.component.html',
  styleUrls: ['./equipments.component.scss'],
  animations: [fuseAnimations],
})
export class EquipmentsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(FilterEquipmentsComponent, { static: true }) filterEquipmentsComponent: FilterEquipmentsComponent;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  public equipments: Equipment[] = [];
  public displayedColumns: string[] = ['number', 'name', 'typeName', 'locationName', 'locationEnvironmentName', 'warrantyStatus', 'warrantyStartDate', 'warrantyEndDate', 'createdAt', 'edit'];
  public totalCount: Number;
  public filtered = false;
  public loading = true;

  private subscriptions = new Subject<void>();
  public readonly equipmentsReload = new Subject<void>();

  public hasEditEquipmentPermission = this.authenticationService.hasPermission('equipments-edit');
  public hasCreateEquipmentPermission = this.authenticationService.hasPermission('equipments-create');
  public hasCreateExportPermission = this.authenticationService.hasPermission('exports-create');
  public hasCreateWarrantiesPermission = this.authenticationService.hasPermission('warranties-create');
  private updateListener = (event: any) => {
    this.reload();
  }

  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly authenticationService: AuthenticationService,
    private readonly equipmentsService: EquipmentsService,
    private readonly matDialog: MatDialog,
    private readonly dialogEquipmentService: DialogEquipmentService,
    public readonly sidenavService: SidenavService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.sort.active = 'name';
    this.sort.direction = 'asc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.filterEquipmentsComponent.filterChanged.pipe(tap(() => this.paginator.firstPage())),
      this.equipmentsReload
    )
      .pipe(
        debounceTime(500),
        takeUntil(this.subscriptions),
        switchMap(() => {
          this.sort.direction = this.sort.direction || 'asc';

          const options = {
            orderBy: `${this.sort.active}_${this.sort.direction}`,
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          this.loading = true;
          return this.equipmentsService.loadData({
            where: this.filterEquipmentsComponent.filterChanged.value,
            options: options
          });
        })
      )
      .subscribe((result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0
        });

        this.equipments = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });

    window.addEventListener('updateWarranty', this.updateListener);
  }

  reload(): void {
    this.equipmentsReload.next();
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
    window.removeEventListener('updateWarranty', this.updateListener);
  }

  async openDialog(equipmentId?: string): Promise<void> {
    const result = await this.dialogEquipmentService.open({
      equipmentId: equipmentId
    });

    if (result && result.reload) {
      this.equipmentsReload.next();
    }
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterEquipmentsComponent.clearFilters();
  }

  public openExportDialog(): void {
    this.matDialog.open(ExportDialogComponent, {
      panelClass: 'export-dialog',
      autoFocus: false,
      data: {
        type: ExportDialogTypeEnum.equipments,
        limit: 10000,
        total: this.totalCount,
        where: this.filterEquipmentsComponent.filterChanged.value
      } as ExportDialogComponentInjectedData
    });
  }
}
